export interface HasDisplayOrder {
    id: string;
    sectionId: string | null;
    displayOrder: number | null | undefined;
}

export function resolveDisplayOrderDuplicates<T extends HasDisplayOrder>(
    rows: T[]
): T[] {
    const bySectionMap = new Map<string | null, T[]>();
    for (const row of rows) {
        const key = row.sectionId ?? null;
        if (!bySectionMap.has(key)) bySectionMap.set(key, []);
        bySectionMap.get(key)!.push({ ...row });
    }

    const resolved = new Map<string, T>();
    bySectionMap.forEach((sectionRows) => {
        sectionRows.sort(
            (a, b) =>
                (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
                String(a.id).localeCompare(String(b.id))
        );
        let prev: number | null = null;
        for (const row of sectionRows) {
            if (prev !== null && (row.displayOrder ?? 0) <= prev) {
                row.displayOrder = prev + 1;
            }
            prev = row.displayOrder ?? 0;
            resolved.set(row.id, row);
        }
    });

    return rows.map((row) => resolved.get(row.id) ?? row);
}

export function applySparseDisplayOrderInsert<T extends HasDisplayOrder>(
    allRows: T[],
    insertRow: T
): T[] {
    const insertOrderRaw = insertRow?.displayOrder;
    const insertOrder =
        typeof insertOrderRaw === 'number' ? insertOrderRaw : Number(insertOrderRaw);
    if (!Number.isFinite(insertOrder) || insertOrder < 1) {
        return allRows;
    }

    const targetSectionId = insertRow.sectionId ?? null;
    const otherRows = allRows.filter((r) => r.id !== insertRow.id);

    const unaffected = otherRows.filter(
        (r) => (r.sectionId ?? null) !== targetSectionId
    );
    const sectionRows = otherRows
        .filter((r) => (r.sectionId ?? null) === targetSectionId)
        .map((r) => ({ ...r }));

    const fixed = sectionRows.filter((r) => (r.displayOrder ?? 0) < insertOrder);
    const toPlace = sectionRows
        .filter((r) => (r.displayOrder ?? 0) >= insertOrder)
        .sort(
            (a, b) =>
                (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
                String(a.id).localeCompare(String(b.id))
        );

    const used = new Set<number>();
    fixed.forEach((r) => {
        if (typeof r.displayOrder === 'number' && r.displayOrder >= 1) {
            used.add(r.displayOrder);
        }
    });
    used.add(insertOrder);

    const nextFree = (start: number) => {
        let v = start;
        while (used.has(v)) v += 1;
        return v;
    };

    const placed = toPlace.map((r) => {
        const desired =
            typeof r.displayOrder === 'number' ? r.displayOrder : Number(r.displayOrder);
        const start = Number.isFinite(desired) ? Math.max(insertOrder, desired, 1) : insertOrder;
        const order = used.has(start) ? nextFree(start) : start;
        used.add(order);
        return { ...(r as T), displayOrder: order };
    });

    return [
        ...unaffected,
        ...fixed,
        ...placed,
        { ...(insertRow as T), displayOrder: insertOrder },
    ];
}

